/**
 * TournamentManager - Local & Online Mini-Tournament Bracket Manager
 * Supports 4 to 8 player single-elimination and double-elimination brackets
 * for game nights, pub gatherings, and online friend groups.
 * Handles match seeding, automatic byes, game progression, manual score overrides,
 * CPU match simulation, and crowning the table champion.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { AchievementManager } from '../achievements/AchievementManager.js';

export const TOURNAMENT_FORMATS = {
  SINGLE_ELIM: 'single',
  DOUBLE_ELIM: 'double'
};

export const TOURNAMENT_SERIES_MODES = {
  BO1: 'bo1',
  BO3: 'bo3',
  BO5: 'bo5'
};

export const TOURNAMENT_TARGET_WINS = {
  bo1: 1,
  bo3: 2,
  bo5: 3
};

export const DEFAULT_AVATARS = ['👑', '🃏', '🍺', '⚡', '🏆', '🎯', '🔥', '🎲'];

export const PRESET_PLAYERS_8 = [
  { id: 'p1', name: 'Player 1 (You)', type: 'human', cpuProfile: 'shark', avatar: '👑', seed: 1 },
  { id: 'p2', name: 'The Shark', type: 'cpu', cpuProfile: 'shark', avatar: '🦈', seed: 2 },
  { id: 'p3', name: 'The Gambler', type: 'cpu', cpuProfile: 'gambler', avatar: '🎲', seed: 3 },
  { id: 'p4', name: 'The Turtler', type: 'cpu', cpuProfile: 'turtler', avatar: '🐢', seed: 4 },
  { id: 'p5', name: 'Pub Master Dave', type: 'human', cpuProfile: 'gambler', avatar: '🍺', seed: 5 },
  { id: 'p6', name: 'Lucky Sarah', type: 'human', cpuProfile: 'shark', avatar: '⚡', seed: 6 },
  { id: 'p7', name: 'Peggin Pete', type: 'cpu', cpuProfile: 'gambler', avatar: '🎯', seed: 7 },
  { id: 'p8', name: 'Cribbage Chris', type: 'cpu', cpuProfile: 'turtler', avatar: '🔥', seed: 8 }
];

export class TournamentManager {
  static STORAGE_KEY = 'crazyCribbage.tournamentState';
  static HISTORY_KEY = 'crazyCribbage.tournamentHistory';
  static SETUP_SETTINGS_KEY = 'crazyCribbage.tournamentSetupSettings';
  static _lastState = null;

  /**
   * Retrieves active tournament state from storage.
   * @param {Object} [storage=null]
   * @returns {Object|null}
   */
  static getTournamentState(storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return TournamentManager._lastState || null;
    try {
      const raw = s.getItem(TournamentManager.STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data && typeof data === 'object') return data;
      }
    } catch (e) {
      console.warn('[TournamentManager] Failed to load state:', e);
    }
    return TournamentManager._lastState || null;
  }

  /**
   * Saves tournament state to storage and updates UI if active.
   * @param {Object|null} state
   * @param {Object} [storage=null]
   */
  static saveTournamentState(state, storage = null) {
    TournamentManager._lastState = state;
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return;
    try {
      if (state === null) {
        s.removeItem(TournamentManager.STORAGE_KEY);
      } else {
        s.setItem(TournamentManager.STORAGE_KEY, JSON.stringify(state));
      }
    } catch (e) {
      console.warn('[TournamentManager] Failed to save state:', e);
    }
  }

  /**
   * Retrieves remembered setup preferences (title, format, seriesMode, playerCount, roster).
   * @param {Object} [storage=null]
   * @returns {Object|null}
   */
  static getSavedSetupSettings(storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return null;
    try {
      const raw = s.getItem(TournamentManager.SETUP_SETTINGS_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data && typeof data === 'object') return data;
      }
    } catch (e) {
      console.warn('[TournamentManager] Failed to load setup settings:', e);
    }
    return null;
  }

  /**
   * Saves tournament setup preferences for future mini-tournaments.
   * @param {Object} settings
   * @param {Object} [storage=null]
   */
  static saveSetupSettings(settings, storage = null) {
    if (!settings || typeof settings !== 'object') return;
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return;
    try {
      s.setItem(TournamentManager.SETUP_SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('[TournamentManager] Failed to save setup settings:', e);
    }
  }

  /**
   * Creates and initializes a new 4-8 player tournament.
   * @param {Object} options
   * @param {string} [options.title='Crazy Cribbage Table Championship']
   * @param {string} [options.format='single'] 'single' | 'double'
   * @param {string} [options.seriesMode='bo3'] 'bo1' | 'bo3' | 'bo5'
   * @param {number} [options.playerCount=4] 4 to 8
   * @param {Array<Object>} [options.players] Array of participant objects
   * @param {string} [options.brand='crazy']
   * @param {number} [options.targetPoints=121]
   * @returns {Object} Newly created tournament state
   */
  static createTournament(options = {}, storage = null) {
    const s = storage || options.storage || null;
    const playerCount = Math.max(4, Math.min(8, Number(options.playerCount) || 4));
    const format = options.format === TOURNAMENT_FORMATS.DOUBLE_ELIM ? TOURNAMENT_FORMATS.DOUBLE_ELIM : TOURNAMENT_FORMATS.SINGLE_ELIM;
    const seriesMode = options.seriesMode || TOURNAMENT_SERIES_MODES.BO3;
    const targetWins = TOURNAMENT_TARGET_WINS[seriesMode] || 2;
    const title = (options.title || 'Table Championship').trim();
    const brand = options.brand || 'crazy';
    const targetPoints = options.targetPoints || 121;

    // Build participant roster
    let players = [];
    if (Array.isArray(options.players) && options.players.length >= playerCount) {
      players = options.players.slice(0, playerCount).map((p, idx) => ({
        id: p.id || `p${idx + 1}`,
        name: (p.name || `Player ${idx + 1}`).trim(),
        type: p.type || (idx === 0 ? 'human' : 'cpu'),
        cpuProfile: p.cpuProfile || 'shark',
        avatar: p.avatar || DEFAULT_AVATARS[idx % DEFAULT_AVATARS.length],
        seed: Number(p.seed) || (idx + 1),
        peerId: p.peerId || null
      }));
    } else {
      players = PRESET_PLAYERS_8.slice(0, playerCount).map((p, idx) => ({
        ...p,
        seed: idx + 1
      }));
    }

    // Sort by seed
    players.sort((a, b) => a.seed - b.seed);

    // Generate bracket matches
    const matches = format === TOURNAMENT_FORMATS.DOUBLE_ELIM
      ? TournamentManager._buildDoubleElimBracket(players, targetWins)
      : TournamentManager._buildSingleElimBracket(players, targetWins);

    const state = {
      id: 'tourney_' + Date.now(),
      title,
      format,
      seriesMode,
      targetWins,
      playerCount,
      players,
      matches,
      activeMatchId: null,
      champion: null,
      podium: { first: null, second: null, third: null },
      createdAt: Date.now(),
      updatedAt: Date.now(),
      brand,
      targetPoints,
      isComplete: false
    };

    // Auto-advance any matches with BYEs
    TournamentManager._autoAdvanceByes(state);

    // Set first available ready match as active
    TournamentManager._updateMatchStatuses(state);
    const firstReady = Object.values(state.matches).find(m => m.status === 'ready');
    if (firstReady) {
      state.activeMatchId = firstReady.id;
    }

    TournamentManager.saveTournamentState(state, s);
    return state;
  }

  /**
   * Builds single elimination bracket tree.
   * Bracket size is 4 or 8.
   * For 5, 6, 7 players, slots are padded with BYE virtual players.
   * Standard seeding pairings:
   * 4-player: (1 vs 4), (2 vs 3) -> Finals
   * 8-player: (1 vs 8), (4 vs 5), (2 vs 7), (3 vs 6) -> Semis -> Finals
   */
  static _buildSingleElimBracket(players, targetWins) {
    const n = players.length;
    const bracketSize = n <= 4 ? 4 : 8;
    const matches = {};

    if (bracketSize === 4) {
      // 4-Player Bracket
      const p1 = players[0] || null;
      const p2 = players[1] || null;
      const p3 = players[2] || null;
      const p4 = players[3] || null;

      matches['m_semi_1'] = {
        id: 'm_semi_1',
        bracket: 'winners',
        round: 1,
        roundLabel: 'Semifinals',
        player1Id: p1 ? p1.id : null,
        player2Id: p4 ? p4.id : null,
        wins: { [p1 ? p1.id : 'p1']: 0, [p4 ? p4.id : 'p4']: 0 },
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'ready',
        nextMatchId: 'm_finals',
        nextMatchSlot: 1,
        loserNextMatchId: 'm_bronze',
        loserNextMatchSlot: 1
      };

      matches['m_semi_2'] = {
        id: 'm_semi_2',
        bracket: 'winners',
        round: 1,
        roundLabel: 'Semifinals',
        player1Id: p2 ? p2.id : null,
        player2Id: p3 ? p3.id : null,
        wins: { [p2 ? p2.id : 'p2']: 0, [p3 ? p3.id : 'p3']: 0 },
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'ready',
        nextMatchId: 'm_finals',
        nextMatchSlot: 2,
        loserNextMatchId: 'm_bronze',
        loserNextMatchSlot: 2
      };

      matches['m_bronze'] = {
        id: 'm_bronze',
        bracket: 'consolation',
        round: 2,
        roundLabel: '3rd Place Playoff',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: null
      };

      matches['m_finals'] = {
        id: 'm_finals',
        bracket: 'winners',
        round: 2,
        roundLabel: 'Championship Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: null
      };

    } else {
      // 8-Player Bracket (with BYE support for 5, 6, 7 players)
      // Seeds in 8 bracket:
      // Match 1: 1 vs 8
      // Match 2: 4 vs 5
      // Match 3: 2 vs 7
      // Match 4: 3 vs 6
      const seedPairings = [
        [1, 8],
        [4, 5],
        [2, 7],
        [3, 6]
      ];

      const getPlayerBySeed = (seedNum) => players.find(p => p.seed === seedNum) || null;

      for (let i = 0; i < 4; i++) {
        const [s1, s2] = seedPairings[i];
        const pA = getPlayerBySeed(s1);
        const pB = getPlayerBySeed(s2);

        matches[`m_qf_${i + 1}`] = {
          id: `m_qf_${i + 1}`,
          bracket: 'winners',
          round: 1,
          roundLabel: 'Quarterfinals',
          player1Id: pA ? pA.id : null,
          player2Id: pB ? pB.id : null,
          wins: {
            [pA ? pA.id : 'pA']: 0,
            [pB ? pB.id : 'pB']: 0
          },
          games: [],
          targetWins,
          winnerId: null,
          loserId: null,
          status: (pA && pB) ? 'ready' : (pA && !pB ? 'bye' : 'pending'),
          nextMatchId: i < 2 ? 'm_semi_1' : 'm_semi_2',
          nextMatchSlot: (i % 2) + 1
        };
      }

      matches['m_semi_1'] = {
        id: 'm_semi_1',
        bracket: 'winners',
        round: 2,
        roundLabel: 'Semifinals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_finals',
        nextMatchSlot: 1,
        loserNextMatchId: 'm_bronze',
        loserNextMatchSlot: 1
      };

      matches['m_semi_2'] = {
        id: 'm_semi_2',
        bracket: 'winners',
        round: 2,
        roundLabel: 'Semifinals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_finals',
        nextMatchSlot: 2,
        loserNextMatchId: 'm_bronze',
        loserNextMatchSlot: 2
      };

      matches['m_bronze'] = {
        id: 'm_bronze',
        bracket: 'consolation',
        round: 3,
        roundLabel: '3rd Place Playoff',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: null
      };

      matches['m_finals'] = {
        id: 'm_finals',
        bracket: 'winners',
        round: 3,
        roundLabel: 'Championship Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: null
      };
    }

    return matches;
  }

  /**
   * Builds double elimination bracket tree (4 or 8 players).
   * Includes Winners Bracket, Losers/Consolation Bracket, and Grand Finals.
   */
  static _buildDoubleElimBracket(players, targetWins) {
    const n = players.length;
    const matches = {};

    if (n <= 4) {
      // 4-Player Double Elimination:
      // Winners Round 1: W_R1_1 (1 vs 4), W_R1_2 (2 vs 3)
      // Winners Finals: W_Finals (W1 vs W2)
      // Losers Round 1: L_R1 (L1 vs L2)
      // Losers Finals: L_Finals (Winner L_R1 vs Loser W_Finals)
      // Grand Finals: G_Finals (Winner W_Finals vs Winner L_Finals)
      const p1 = players[0] || null;
      const p2 = players[1] || null;
      const p3 = players[2] || null;
      const p4 = players[3] || null;

      matches['m_wb_r1_1'] = {
        id: 'm_wb_r1_1',
        bracket: 'winners',
        round: 1,
        roundLabel: 'Winners Semis 1',
        player1Id: p1 ? p1.id : null,
        player2Id: p4 ? p4.id : null,
        wins: { [p1 ? p1.id : 'p1']: 0, [p4 ? p4.id : 'p4']: 0 },
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'ready',
        nextMatchId: 'm_wb_finals',
        nextMatchSlot: 1,
        loserNextMatchId: 'm_lb_r1',
        loserNextMatchSlot: 1
      };

      matches['m_wb_r1_2'] = {
        id: 'm_wb_r1_2',
        bracket: 'winners',
        round: 1,
        roundLabel: 'Winners Semis 2',
        player1Id: p2 ? p2.id : null,
        player2Id: p3 ? p3.id : null,
        wins: { [p2 ? p2.id : 'p2']: 0, [p3 ? p3.id : 'p3']: 0 },
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'ready',
        nextMatchId: 'm_wb_finals',
        nextMatchSlot: 2,
        loserNextMatchId: 'm_lb_r1',
        loserNextMatchSlot: 2
      };

      matches['m_wb_finals'] = {
        id: 'm_wb_finals',
        bracket: 'winners',
        round: 2,
        roundLabel: 'Winners Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_grand_finals',
        nextMatchSlot: 1,
        loserNextMatchId: 'm_lb_finals',
        loserNextMatchSlot: 2
      };

      matches['m_lb_r1'] = {
        id: 'm_lb_r1',
        bracket: 'losers',
        round: 1,
        roundLabel: 'Losers Round 1',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_lb_finals',
        nextMatchSlot: 1
      };

      matches['m_lb_finals'] = {
        id: 'm_lb_finals',
        bracket: 'losers',
        round: 2,
        roundLabel: 'Losers Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_grand_finals',
        nextMatchSlot: 2
      };

      matches['m_grand_finals'] = {
        id: 'm_grand_finals',
        bracket: 'finals',
        round: 3,
        roundLabel: 'Grand Championship Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: null
      };

    } else {
      // 5-8 Player Double Elimination
      const getPlayerBySeed = (seedNum) => players.find(p => p.seed === seedNum) || null;
      const seedPairings = [
        [1, 8],
        [4, 5],
        [2, 7],
        [3, 6]
      ];

      for (let i = 0; i < 4; i++) {
        const [s1, s2] = seedPairings[i];
        const pA = getPlayerBySeed(s1);
        const pB = getPlayerBySeed(s2);

        matches[`m_wb_qf_${i + 1}`] = {
          id: `m_wb_qf_${i + 1}`,
          bracket: 'winners',
          round: 1,
          roundLabel: 'Winners Quarterfinals',
          player1Id: pA ? pA.id : null,
          player2Id: pB ? pB.id : null,
          wins: {},
          games: [],
          targetWins,
          winnerId: null,
          loserId: null,
          status: (pA && pB) ? 'ready' : (pA && !pB ? 'bye' : 'pending'),
          nextMatchId: i < 2 ? 'm_wb_semi_1' : 'm_wb_semi_2',
          nextMatchSlot: (i % 2) + 1,
          loserNextMatchId: i < 2 ? 'm_lb_r1_1' : 'm_lb_r1_2',
          loserNextMatchSlot: (i % 2) + 1
        };
      }

      matches['m_wb_semi_1'] = {
        id: 'm_wb_semi_1',
        bracket: 'winners',
        round: 2,
        roundLabel: 'Winners Semifinals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_wb_finals',
        nextMatchSlot: 1,
        loserNextMatchId: 'm_lb_r2_1',
        loserNextMatchSlot: 2
      };

      matches['m_wb_semi_2'] = {
        id: 'm_wb_semi_2',
        bracket: 'winners',
        round: 2,
        roundLabel: 'Winners Semifinals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_wb_finals',
        nextMatchSlot: 2,
        loserNextMatchId: 'm_lb_r2_2',
        loserNextMatchSlot: 2
      };

      matches['m_wb_finals'] = {
        id: 'm_wb_finals',
        bracket: 'winners',
        round: 3,
        roundLabel: 'Winners Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_grand_finals',
        nextMatchSlot: 1,
        loserNextMatchId: 'm_lb_finals',
        loserNextMatchSlot: 2
      };

      // Losers Bracket for 8
      matches['m_lb_r1_1'] = {
        id: 'm_lb_r1_1',
        bracket: 'losers',
        round: 1,
        roundLabel: 'Losers Round 1',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_lb_r2_1',
        nextMatchSlot: 1
      };

      matches['m_lb_r1_2'] = {
        id: 'm_lb_r1_2',
        bracket: 'losers',
        round: 1,
        roundLabel: 'Losers Round 1',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_lb_r2_2',
        nextMatchSlot: 1
      };

      matches['m_lb_r2_1'] = {
        id: 'm_lb_r2_1',
        bracket: 'losers',
        round: 2,
        roundLabel: 'Losers Semis 1',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_lb_semi',
        nextMatchSlot: 1
      };

      matches['m_lb_r2_2'] = {
        id: 'm_lb_r2_2',
        bracket: 'losers',
        round: 2,
        roundLabel: 'Losers Semis 2',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_lb_semi',
        nextMatchSlot: 2
      };

      matches['m_lb_semi'] = {
        id: 'm_lb_semi',
        bracket: 'losers',
        round: 3,
        roundLabel: 'Losers Semifinals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_lb_finals',
        nextMatchSlot: 1
      };

      matches['m_lb_finals'] = {
        id: 'm_lb_finals',
        bracket: 'losers',
        round: 4,
        roundLabel: 'Losers Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: 'm_grand_finals',
        nextMatchSlot: 2
      };

      matches['m_grand_finals'] = {
        id: 'm_grand_finals',
        bracket: 'finals',
        round: 5,
        roundLabel: 'Grand Championship Finals',
        player1Id: null,
        player2Id: null,
        wins: {},
        games: [],
        targetWins,
        winnerId: null,
        loserId: null,
        status: 'pending',
        nextMatchId: null
      };
    }

    return matches;
  }

  /**
   * Advances matches with BYEs automatically.
   */
  static _autoAdvanceByes(state) {
    if (!state || !state.matches) return;
    let changed = false;

    for (const match of Object.values(state.matches)) {
      if (match.status === 'bye' && match.player1Id && !match.player2Id) {
        match.winnerId = match.player1Id;
        match.status = 'completed';
        TournamentManager._advanceWinner(state, match);
        changed = true;
      }
    }

    if (changed) {
      TournamentManager._updateMatchStatuses(state);
    }
  }

  /**
   * Updates match statuses based on player presence.
   */
  static _updateMatchStatuses(state) {
    if (!state || !state.matches) return;

    for (const match of Object.values(state.matches)) {
      if (match.status === 'completed') continue;

      if (match.player1Id && match.player2Id) {
        match.status = (match.games && match.games.length > 0) ? 'in_progress' : 'ready';
      } else if (match.player1Id && !match.player2Id && match.round === 1) {
        match.status = 'bye';
      } else {
        match.status = 'pending';
      }
    }
  }

  /**
   * Advances a match winner to their next tournament slot.
   */
  static _advanceWinner(state, match) {
    if (!match.winnerId) return;

    // Advance winner
    if (match.nextMatchId && state.matches[match.nextMatchId]) {
      const nextM = state.matches[match.nextMatchId];
      if (match.nextMatchSlot === 1) {
        nextM.player1Id = match.winnerId;
      } else if (match.nextMatchSlot === 2) {
        nextM.player2Id = match.winnerId;
      } else {
        if (!nextM.player1Id) nextM.player1Id = match.winnerId;
        else if (!nextM.player2Id) nextM.player2Id = match.winnerId;
      }
    }

    // Advance loser if consolation / losers bracket exists
    if (match.loserId && match.loserNextMatchId && state.matches[match.loserNextMatchId]) {
      const loserM = state.matches[match.loserNextMatchId];
      if (match.loserNextMatchSlot === 1) {
        loserM.player1Id = match.loserId;
      } else if (match.loserNextMatchSlot === 2) {
        loserM.player2Id = match.loserId;
      } else {
        if (!loserM.player1Id) loserM.player1Id = match.loserId;
        else if (!loserM.player2Id) loserM.player2Id = match.loserId;
      }
    }

    TournamentManager._checkChampionship(state);
  }

  /**
   * Checks if tournament finals have completed and crowns champion.
   */
  static _checkChampionship(state) {
    if (!state || !state.matches) return;

    const finals = state.matches['m_grand_finals'] || state.matches['m_finals'];
    if (finals && finals.status === 'completed' && finals.winnerId) {
      state.champion = finals.winnerId;
      state.isComplete = true;

      // Determine 2nd place
      const secondPlace = finals.loserId || null;

      // Determine 3rd place from bronze playoff or loser of losers finals
      let thirdPlace = null;
      if (state.matches['m_bronze'] && state.matches['m_bronze'].status === 'completed') {
        thirdPlace = state.matches['m_bronze'].winnerId;
      } else if (state.matches['m_lb_finals'] && state.matches['m_lb_finals'].status === 'completed') {
        thirdPlace = state.matches['m_lb_finals'].loserId;
      }

      state.podium = {
        first: state.champion,
        second: secondPlace,
        third: thirdPlace
      };

      // Archive tournament result to history
      TournamentManager._archiveTournament(state);

      // Trigger tournament achievements if human player won
      try {
        const champPlayer = TournamentManager.getPlayer(state, state.champion);
        const isHumanChamp = champPlayer && (champPlayer.type === 'human' || champPlayer.id === 'p1');
        if (isHumanChamp) {
          if (typeof AchievementManager !== 'undefined' && typeof AchievementManager.recordTournamentWin === 'function') {
            AchievementManager.recordTournamentWin(state);
          }
        }
      } catch (e) {}
    }
  }

  /**
   * Saves completed tournament to local history list.
   */
  static _archiveTournament(state) {
    try {
      const s = (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
      if (!s) return;
      const historyRaw = s.getItem(TournamentManager.HISTORY_KEY);
      let history = [];
      if (historyRaw) {
        try { history = JSON.parse(historyRaw) || []; } catch (e) {}
      }
      history.unshift({
        id: state.id,
        title: state.title,
        format: state.format,
        playerCount: state.playerCount,
        champion: state.champion,
        championName: TournamentManager.getPlayer(state, state.champion)?.name || 'Champion',
        completedAt: Date.now()
      });
      if (history.length > 20) history = history.slice(0, 20);
      s.setItem(TournamentManager.HISTORY_KEY, JSON.stringify(history));
    } catch (e) {}
  }

  /**
   * Returns participant object by ID.
   */
  static getPlayer(state, playerId) {
    if (!state || !state.players) return null;
    return state.players.find(p => p.id === playerId) || null;
  }

  /**
   * Returns current active tournament match.
   */
  static getActiveMatch(state = null) {
    const s = state || TournamentManager.getTournamentState();
    if (!s || !s.matches || !s.activeMatchId) return null;
    return s.matches[s.activeMatchId] || null;
  }

  /**
   * Sets active match ID and saves state.
   */
  static setActiveMatch(matchId, storage = null) {
    const state = TournamentManager.getTournamentState(storage);
    if (!state || !state.matches || !state.matches[matchId]) return null;
    state.activeMatchId = matchId;
    state.updatedAt = Date.now();
    TournamentManager.saveTournamentState(state, storage);
    return state.matches[matchId];
  }

  /**
   * Records completed game result into the active tournament match.
   * Called by GameEngine.checkGameOver.
   * @param {Object} gameResult
   * @param {string} gameResult.winner Winning seat ('you'|'cpu'|'p1'|'p2')
   * @param {number} [gameResult.p1Score=0]
   * @param {number} [gameResult.p2Score=0]
   * @param {string} [gameResult.skunkKind]
   * @param {Object} [storage=null]
   * @returns {Object|null}
   */
  static recordGame(gameResult = {}, storage = null) {
    const state = TournamentManager.getTournamentState(storage);
    if (!state || !state.activeMatchId) return null;

    const match = state.matches[state.activeMatchId];
    if (!match || match.status === 'completed') return null;

    const p1 = TournamentManager.getPlayer(state, match.player1Id);
    const p2 = TournamentManager.getPlayer(state, match.player2Id);
    if (!p1 || !p2) return null;

    // Map winner seat to participant id
    let winningPlayerId = null;
    const w = gameResult.winner;
    if (match.seatMap) {
      const mappedKey = (w === 'you') ? 'p1' : ((w === 'cpu') ? 'p2' : w);
      winningPlayerId = match.seatMap[mappedKey] || match.seatMap[w];
    }
    if (!winningPlayerId) {
      if (w === 'p1' || w === 'you') {
        winningPlayerId = p1.id;
      } else if (w === 'p2' || w === 'cpu') {
        winningPlayerId = p2.id;
      } else if (w === p1.id || w === p2.id) {
        winningPlayerId = w;
      } else {
        winningPlayerId = p1.id;
      }
    }

    match.wins = match.wins || {};
    match.wins[winningPlayerId] = (match.wins[winningPlayerId] || 0) + 1;

    match.games = match.games || [];
    match.games.push({
      gameNumber: match.games.length + 1,
      winnerId: winningPlayerId,
      p1Score: gameResult.p1Score || 0,
      p2Score: gameResult.p2Score || 0,
      skunkKind: gameResult.skunkKind || null,
      timestamp: Date.now()
    });

    const losingPlayerId = winningPlayerId === p1.id ? p2.id : p1.id;
    const targetWins = match.targetWins || state.targetWins || 2;
    let matchFinished = false;

    if (match.wins[winningPlayerId] >= targetWins) {
      match.winnerId = winningPlayerId;
      match.loserId = losingPlayerId;
      match.status = 'completed';
      matchFinished = true;
      TournamentManager._advanceWinner(state, match);
    } else {
      match.status = 'in_progress';
    }

    // If match finished, auto-select next available ready match
    if (matchFinished && !state.isComplete) {
      TournamentManager._updateMatchStatuses(state);
      const nextReady = Object.values(state.matches).find(m => m.status === 'ready' && m.id !== match.id);
      if (nextReady) {
        state.activeMatchId = nextReady.id;
      }
    }

    state.updatedAt = Date.now();
    TournamentManager.saveTournamentState(state, storage);

    return {
      state,
      match,
      matchFinished,
      winner: TournamentManager.getPlayer(state, match.winnerId),
      isTournamentComplete: state.isComplete,
      champion: state.champion ? TournamentManager.getPlayer(state, state.champion) : null
    };
  }

  /**
   * Manually sets or overrides a match winner (useful for physical pub boards where matches
   * are played on wooden boards and results are entered into the app bracket manager).
   * @param {string} matchId
   * @param {string} winnerId
   * @param {number} [winnerWins=1]
   * @param {number} [loserWins=0]
   * @param {Object} [storage=null]
   * @returns {Object|null}
   */
  static manualSetMatchWinner(matchId, winnerId, winnerWins = null, loserWins = 0, storage = null) {
    const state = TournamentManager.getTournamentState(storage);
    if (!state || !state.matches || !state.matches[matchId]) return null;

    const match = state.matches[matchId];
    if (!match.player1Id || !match.player2Id) return null;
    if (winnerId !== match.player1Id && winnerId !== match.player2Id) return null;

    const loserId = winnerId === match.player1Id ? match.player2Id : match.player1Id;
    const requiredWins = match.targetWins || state.targetWins || 1;
    const wWins = winnerWins !== null ? winnerWins : requiredWins;

    match.wins = {
      [winnerId]: wWins,
      [loserId]: loserWins
    };
    match.winnerId = winnerId;
    match.loserId = loserId;
    match.status = 'completed';
    match.games = match.games || [];
    match.games.push({
      gameNumber: match.games.length + 1,
      winnerId,
      p1Score: 121,
      p2Score: 90,
      manual: true,
      timestamp: Date.now()
    });

    TournamentManager._advanceWinner(state, match);
    TournamentManager._updateMatchStatuses(state);

    if (!state.isComplete) {
      const nextReady = Object.values(state.matches).find(m => m.status === 'ready' && m.id !== match.id);
      if (nextReady) state.activeMatchId = nextReady.id;
    }

    state.updatedAt = Date.now();
    TournamentManager.saveTournamentState(state, storage);

    return {
      state,
      match,
      winner: TournamentManager.getPlayer(state, winnerId),
      isTournamentComplete: state.isComplete
    };
  }

  /**
   * Simulates a match between two Computer players automatically.
   * @param {string} matchId
   * @param {Object} [storage=null]
   */
  static simulateCpuMatch(matchId, storage = null) {
    const state = TournamentManager.getTournamentState(storage);
    if (!state || !state.matches || !state.matches[matchId]) return null;

    const match = state.matches[matchId];
    const p1 = TournamentManager.getPlayer(state, match.player1Id);
    const p2 = TournamentManager.getPlayer(state, match.player2Id);
    if (!p1 || !p2) return null;

    // Simulation weighting based on AI profile
    const profileWeights = {
      shark: 1.15,
      gambler: 1.0,
      turtler: 0.95
    };
    const w1 = profileWeights[p1.cpuProfile] || 1.0;
    const w2 = profileWeights[p2.cpuProfile] || 1.0;
    const probP1 = w1 / (w1 + w2);

    const targetWins = match.targetWins || state.targetWins || 2;
    let p1Wins = match.wins[p1.id] || 0;
    let p2Wins = match.wins[p2.id] || 0;

    while (p1Wins < targetWins && p2Wins < targetWins) {
      const p1WonGame = Math.random() < probP1;
      const gameWinnerId = p1WonGame ? p1.id : p2.id;
      if (p1WonGame) p1Wins++;
      else p2Wins++;

      const winningScore = state.targetPoints || 121;
      const losingScore = Math.floor(Math.random() * 35) + (winningScore - 40);
      match.games.push({
        gameNumber: match.games.length + 1,
        winnerId: gameWinnerId,
        p1Score: p1WonGame ? winningScore : losingScore,
        p2Score: p1WonGame ? losingScore : winningScore,
        simulated: true,
        timestamp: Date.now()
      });
    }

    const winnerId = p1Wins >= targetWins ? p1.id : p2.id;
    return TournamentManager.manualSetMatchWinner(matchId, winnerId, Math.max(p1Wins, p2Wins), Math.min(p1Wins, p2Wins), storage);
  }

  /**
   * Resets active tournament.
   */
  static resetTournament(storage = null) {
    TournamentManager.saveTournamentState(null, storage);
  }

  /**
   * Generates a clean text / markdown recap of tournament standings for sharing
   * to group chats, Discord, or WhatsApp.
   * @param {Object} [state=null]
   * @returns {string}
   */
  static generateShareSummary(state = null) {
    const s = state || TournamentManager.getTournamentState();
    if (!s) return 'No active tournament found.';

    const champ = s.champion ? TournamentManager.getPlayer(s, s.champion) : null;
    const firstP = s.podium?.first ? TournamentManager.getPlayer(s, s.podium.first) : null;
    const secondP = s.podium?.second ? TournamentManager.getPlayer(s, s.podium.second) : null;
    const thirdP = s.podium?.third ? TournamentManager.getPlayer(s, s.podium.third) : null;

    const lines = [
      `🏆 **${s.title.toUpperCase()}** 🏆`,
      `Format: ${s.format === 'double' ? 'Double Elimination' : 'Single Elimination'} (${s.playerCount} Players · ${s.seriesMode.toUpperCase()})`,
      `Date: ${new Date(s.createdAt).toLocaleDateString()}`,
      ''
    ];

    if (champ) {
      lines.push(`👑 **CHAMPION: ${champ.name}**`);
      if (secondP) lines.push(`🥈 Runner-Up: ${secondP.name}`);
      if (thirdP) lines.push(`🥉 3rd Place: ${thirdP.name}`);
      lines.push('');
    }

    lines.push('📋 **Match Results:**');
    for (const match of Object.values(s.matches)) {
      const p1 = TournamentManager.getPlayer(s, match.player1Id);
      const p2 = TournamentManager.getPlayer(s, match.player2Id);
      const p1Name = p1 ? p1.name : (match.status === 'bye' ? 'BYE' : 'TBD');
      const p2Name = p2 ? p2.name : (match.status === 'bye' ? 'BYE' : 'TBD');
      const p1Wins = match.wins[match.player1Id] || 0;
      const p2Wins = match.wins[match.player2Id] || 0;

      let statusMarker = '⏳';
      if (match.status === 'completed') statusMarker = '✔';
      else if (match.status === 'in_progress') statusMarker = '▶';
      else if (match.status === 'bye') statusMarker = '⚡';

      lines.push(`${statusMarker} ${match.roundLabel}: ${p1Name} (${p1Wins}) vs ${p2Name} (${p2Wins})`);
    }

    lines.push('');
    lines.push('Played on Crazy Cribbage · 100% Free Online Cribbage');
    return lines.join('\n');
  }
}
